import React from 'react';
import PortfolioChartBox from './PortfolioChartBox';

interface CryptoPortfolioCardProps {
  portfolio: any;
  actualRatio: number;
  chartPortfolios: any[];
  cashAmount: number;
  currentPrices: any;
  currency: string;
  loading: boolean;
  includeCash: boolean;
  isAdmin?: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
}

// 1 บล็อก = 5% ของพอร์ตรวม
const BLOCK_COUNT = 20;
const BLOCK_STEP = 100 / BLOCK_COUNT;

// ไล่สีจากส้ม (#f7931a) ไปม่วง (#8b5cf6) ตามตำแหน่งบล็อก
const blockColor = (index: number) => {
  const t = index / (BLOCK_COUNT - 1);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `rgb(${mix(247, 139)}, ${mix(147, 92)}, ${mix(26, 246)})`;
};

const CryptoPortfolioCard: React.FC<CryptoPortfolioCardProps> = ({
  portfolio: p,
  actualRatio,
  chartPortfolios,
  cashAmount,
  currentPrices,
  currency,
  loading,
  includeCash,
  isAdmin,
  onOpen,
  onEdit,
  onDelete,
  onToggleVisibility,
}) => {
  const targetRatio = Number(p.expectedRatio) || 0;
  const filledBlocks = Math.min(BLOCK_COUNT, Math.round(actualRatio / BLOCK_STEP));
  const targetBlocks = Math.min(BLOCK_COUNT, Math.round(targetRatio / BLOCK_STEP));
  const gap = actualRatio - targetRatio;
  const costBasis = p.settings?.costBasis;

  return (
    <div
      onClick={onOpen}
      className="crypto-port-card w-full p-4 cursor-pointer flex flex-col transition-transform hover:scale-[1.02]"
    >
      {/* ส่วนหัว: ไอคอนเหรียญ + ชื่อ + ป้ายตลาด 24 ชม. */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="shrink-0 w-10 h-10 flex items-center justify-center"
            style={{
              clipPath: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)',
              background: 'linear-gradient(135deg, #f7931a, #8b5cf6)',
            }}
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6h5a3 3 0 010 6H9V6zm0 6h6a3 3 0 010 6H9v-6zM7 6h2M7 18h2M11 4v2M11 18v2M14 4v2M14 18v2" />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-xl font-bold truncate" style={{ color: 'var(--text-color)' }}>
              {p.name}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
              <span className="relative flex w-2 h-2">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
              </span>
              ตลาดคริปโตเปิด 24 ชม.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button
              type="button"
              className="bg-yellow-400 text-black rounded shadow px-2 py-1 text-[10px] font-bold"
              onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
            >
              นำเข้า
            </button>
          )}
          {costBasis?.preset && (
            <span className="text-[11px] font-bold px-2 py-1 rounded-md border border-violet-400/30 bg-violet-500/10 text-violet-300">
              {costBasis.preset === 'CUSTOM' ? costBasis.method : costBasis.preset}
            </span>
          )}
        </div>
      </div>

      {/* แถบบล็อก: สัดส่วนจริง เทียบ เป้าหมาย */}
      {p.expectedRatio !== undefined && (
        <div className="mb-3">
          <div className="flex items-baseline justify-between text-xs mb-1.5 tabular-nums">
            <span style={{ color: 'var(--text-color)' }}>
              ถือจริง <span className="font-bold text-sm">{actualRatio.toFixed(2)}%</span>
            </span>
            <span className="text-gray-400">
              เป้าหมาย <span className="font-bold" style={{ color: 'var(--yellow-text-color)' }}>{targetRatio}%</span>
            </span>
          </div>
          <div
            className="grid gap-[3px]"
            style={{ gridTemplateColumns: `repeat(${BLOCK_COUNT}, minmax(0, 1fr))` }}
            role="img"
            aria-label={`สัดส่วนจริง ${actualRatio.toFixed(2)}% จากเป้าหมาย ${targetRatio}%`}
          >
            {Array.from({ length: BLOCK_COUNT }).map((_, i) => {
              const isFilled = i < filledBlocks;
              const isTarget = i < targetBlocks;
              return (
                <div
                  key={i}
                  className="crypto-block"
                  style={
                    isFilled
                      ? { backgroundColor: blockColor(i) }
                      : isTarget
                        ? { border: '1px dashed rgba(247, 147, 26, 0.55)' }
                        : { backgroundColor: 'rgba(148, 163, 184, 0.12)' }
                  }
                />
              );
            })}
          </div>
          {targetRatio > 0 && Math.abs(gap) >= 0.5 && (
            <div className={`mt-1.5 text-[11px] font-medium tabular-nums ${gap > 0 ? 'text-orange-400' : 'text-gray-400'}`}>
              {gap > 0
                ? `เกินเป้า ${gap.toFixed(2)}%`
                : `ยังขาดอีก ${Math.abs(gap).toFixed(2)}% จะถึงเป้า`}
            </div>
          )}
        </div>
      )}

      <PortfolioChartBox
        portfolios={chartPortfolios}
        currentPrices={currentPrices}
        useSubPorts={true}
        fromAdmin={false}
        currency={currency}
        loading={loading}
        cashAmount={cashAmount}
        includeCash={includeCash}
        layout="horizontal"
        isCrypto
      />

      <div className="flex items-center mt-4">
        <button
          type="button"
          className="w-20 h-10 flex items-center justify-center rounded text-white font-bold bg-red-500/80 hover:bg-red-500"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          ลบ
        </button>
        <button
          type="button"
          className="w-full h-10 ml-2 rounded text-[#18181b] font-bold bg-yellow-400/80 hover:bg-yellow-400"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
          แก้ไข ชื่อพอร์ต / สัดส่วน
        </button>
      </div>
    </div>
  );
};

export default CryptoPortfolioCard;
